// frontend/src/askos/liveSessions/JoinLivePage.js
//
// Student's branded live-session attend page. Top-level route (bypasses
// the AskOS dashboard chrome), auth-checked via the existing askos_token
// convention. Joins Agora as audience-only (no publish capability — this
// is a broadcast, not a video call), shows Q&A, and prompts CSAT feedback
// once the session has completed.
//
// Multilingual dubbing: video always comes from the session's source
// channel; audio comes from either that same channel (Original) or a
// separate per-language channel the relay publishes into (Translated).
// Two Agora clients are used only when those two channels differ — one
// client when the student picks Original, two when they pick a dubbed
// language.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { Spinner, Alert, Form } from 'react-bootstrap';
import {
    getLiveSessionStudent, joinLiveSession, heartbeatLiveSession, leaveLiveSession,
    getRecordingUrlStudent, getTranslatedRecordingsStudent,
} from '../../api/liveSessionsApi';
import LiveQAWidget from './LiveQAWidget';
import LiveTranscriptPanel from './LiveTranscriptPanel';
import SessionFeedbackModal from './SessionFeedbackModal';
import '../../liveSessionBroadcast.css';

const NAVY = '#092957';
const GOLD = '#f1a51c';
const HEARTBEAT_MS = 25000;

const LANGUAGE_LABEL = { hi: 'Hindi', en: 'English', ta: 'Tamil', te: 'Telugu' };
const sourceLabel = (sourceLanguage) => {
    const code = (sourceLanguage || '').split('-')[0].toLowerCase();
    return LANGUAGE_LABEL[code] || 'Original';
};

const JoinLivePage = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const slug = 'samkalp-ias';
    const videoClientRef = useRef(null);
    const audioClientRef = useRef(null);
    const audioContainerRef = useRef(null);
    const remoteAudioTrackRef = useRef(null);
    // Ref mirror of speakerMuted so the 'user-published' closure (registered
    // once per doJoin call) always reads the latest mute state rather than
    // whatever it was when the listener was created.
    const speakerMutedRef = useRef(false);
    // Synchronous reentrancy guard — `joining` state-based disabling only
    // takes effect after the next render, so a fast double-tap on a
    // touchscreen (touchend + synthesized click, or a genuine double-tap)
    // can call doJoin() twice before the button actually disables. A ref
    // updates immediately, closing that race.
    const joiningRef = useRef(false);

    const [session, setSession] = useState(null);
    const [error, setError] = useState('');
    const [joined, setJoined] = useState(false);
    const [joining, setJoining] = useState(false);
    const [speakerMuted, setSpeakerMuted] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [dubbingFellBack, setDubbingFellBack] = useState(false);
    const [recordingUrl, setRecordingUrl] = useState(null);
    const [translatedRecordings, setTranslatedRecordings] = useState([]);
    const [playbackLanguage, setPlaybackLanguage] = useState('source');

    useEffect(() => { speakerMutedRef.current = speakerMuted; }, [speakerMuted]);

    const loadSession = useCallback(() => {
        getLiveSessionStudent(slug, sessionId)
            .then(s => {
                setSession(s);
                if (s.status === 'completed' && !s.my_feedback_submitted) setShowFeedback(true);
            })
            .catch(() => setError('Could not load this session.'));
    }, [sessionId]);

    useEffect(() => {
        loadSession();
        const interval = setInterval(loadSession, 15000);
        return () => clearInterval(interval);
    }, [loadSession]);

    useEffect(() => {
        if (session?.status === 'completed' && session.recording_status === 'available' && !recordingUrl) {
            getRecordingUrlStudent(slug, sessionId).then(r => setRecordingUrl(r.url)).catch(() => {});
        }
    }, [session, sessionId, recordingUrl]);

    useEffect(() => {
        if (session?.status === 'completed' && (session.target_languages || []).length > 0) {
            getTranslatedRecordingsStudent(slug, sessionId).then(setTranslatedRecordings).catch(() => {});
        }
    }, [session, sessionId]);

    const activeRecordingUrl = playbackLanguage === 'source'
        ? recordingUrl
        : translatedRecordings.find(r => r.language === playbackLanguage)?.url;

    const leaveClients = useCallback(async () => {
        await audioClientRef.current?.leave().catch(() => {});
        audioClientRef.current = null;
        await videoClientRef.current?.leave().catch(() => {});
        videoClientRef.current = null;
        remoteAudioTrackRef.current = null;
    }, []);

    const doJoin = async (languageOverride) => {
        if (joiningRef.current) return;
        joiningRef.current = true;
        setError('');
        setJoining(true);
        // Whatever's actually sent to the backend — used both for the API
        // call and to decide whether a 409 should trigger the automatic
        // fall-back-to-source retry below (must match, or a pre-selected
        // translated language picked before ever joining wouldn't retry).
        const requestedLanguage = languageOverride || selectedLanguage || null;
        try {
            // Leave-before-switch: never run two joins for the same student
            // at once (avoids double audio if this is a language change).
            await leaveClients();

            const result = await joinLiveSession(slug, sessionId, requestedLanguage);
            const { video, audio, selection, relay_status: relayStatusResult } = result;
            setSelectedLanguage(selection);

            const dubbingUnavailable = selection !== 'source' && relayStatusResult && relayStatusResult !== 'running';
            setDubbingFellBack(dubbingUnavailable);
            // Server never auto-falls-back audio to source — that's this
            // page's job, using the relay_status it exposes.
            const shouldPlayVideoAudio = video.play_audio || dubbingUnavailable;

            const videoClient = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
            // level: 1 = AUDIENCE_LEVEL_LOW_LATENCY (~1500-2000ms) — this is what
            // makes usage bill as Broadcast Streaming ($0.59/1k min, CDN-optimized
            // for large one-to-many audiences) instead of Interactive Live
            // Streaming ($0.99/1k min, ultra-low-latency, the SDK's default when
            // this option is omitted). Same channel profile either way — this
            // option is the only thing that determines which product it bills as.
            await videoClient.setClientRole('audience', { level: 1 });
            await videoClient.join(video.appId, video.channel, video.token, video.uid || null);
            videoClient.on('user-published', async (user, mediaType) => {
                await videoClient.subscribe(user, mediaType);
                if (mediaType === 'audio' && shouldPlayVideoAudio) {
                    remoteAudioTrackRef.current = user.audioTrack;
                    user.audioTrack?.setVolume(speakerMutedRef.current ? 0 : 100);
                    user.audioTrack?.play();
                }
                if (mediaType === 'video' && audioContainerRef.current) user.videoTrack?.play(audioContainerRef.current);
            });
            videoClientRef.current = videoClient;

            if (!shouldPlayVideoAudio && audio.channel !== video.channel) {
                const audioClient = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
                await audioClient.setClientRole('audience', { level: 1 });
                await audioClient.join(video.appId, audio.channel, audio.token, audio.uid || null);
                audioClient.on('user-published', async (user, mediaType) => {
                    if (mediaType !== 'audio') return;
                    await audioClient.subscribe(user, mediaType);
                    remoteAudioTrackRef.current = user.audioTrack;
                    user.audioTrack?.setVolume(speakerMutedRef.current ? 0 : 100);
                    user.audioTrack?.play();
                });
                audioClientRef.current = audioClient;
            }

            setJoined(true);
        } catch (err) {
            // The backend rejects an explicit request for a translated
            // language with 409 once relay_status isn't "running" (avoids
            // ever joining a channel with no publisher on it) — fall back
            // to the original-language stream automatically rather than
            // just showing the student a dead end.
            if (err?.response?.status === 409 && requestedLanguage && requestedLanguage !== 'source') {
                setDubbingFellBack(true);
                // Release the guard before recursing — doJoin('source')
                // re-acquires it itself; leaving it held here would make
                // the retry immediately no-op against its own lock.
                joiningRef.current = false;
                await doJoin('source');
                return;
            }
            setError(err?.response?.data?.detail || err?.message || 'Could not join the live session.');
        } finally {
            joiningRef.current = false;
            setJoining(false);
        }
    };

    const changeLanguage = async (newLanguage) => {
        setSelectedLanguage(newLanguage);
        if (joined) await doJoin(newLanguage);
    };

    const toggleSpeaker = () => {
        const next = !speakerMuted;
        remoteAudioTrackRef.current?.setVolume(next ? 0 : 100);
        setSpeakerMuted(next);
    };

    useEffect(() => {
        if (!joined) return undefined;
        const interval = setInterval(() => heartbeatLiveSession(slug, sessionId).catch(() => {}), HEARTBEAT_MS);
        return () => clearInterval(interval);
    }, [joined, sessionId]);

    useEffect(() => () => {
        leaveClients();
        if (joined) leaveLiveSession(slug, sessionId).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!session) {
        return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: NAVY }}><Spinner animation="border" variant="light" /></div>;
    }

    const languageOptions = [
        { code: 'source', label: `Original (${sourceLabel(session.source_language)})` },
        ...(session.target_languages || []).map(code => ({ code, label: LANGUAGE_LABEL[code] || code })),
    ];

    return (
        <div className="live-broadcast-shell" style={{ minHeight: '100vh', background: NAVY, color: '#fff' }}>
            <header className="live-header" style={{ padding: '20px 28px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                <div>
                    <div style={{ color: GOLD, fontWeight: 700, fontSize: 12, letterSpacing: '0.05em' }}>SAMKALP IAS ACADEMY — LIVE SESSION</div>
                    <h2 style={{ margin: 0 }}>{session.topic}</h2>
                    <p style={{ margin: 0, color: '#c7d4ea' }}>{session.faculty_name} {session.faculty_designation ? `— ${session.faculty_designation}` : ''}</p>
                </div>
                <button type="button" onClick={() => navigate('/samkalp-ias/live-sessions')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '8px 16px', height: 'fit-content' }}>
                    Back to Sessions
                </button>
            </header>

            {error && <Alert variant="danger" className="m-3 py-2 small">{error}</Alert>}

            {session.status === 'scheduled' && (
                <div style={{ padding: 48, textAlign: 'center' }}>
                    <p style={{ fontSize: 18 }}>This session hasn't started yet.</p>
                    <p style={{ color: '#c7d4ea' }}>Scheduled for {new Date(session.scheduled_start_utc).toLocaleString()}.</p>
                </div>
            )}

            {session.status === 'live' && (
                <div className="live-body" style={{ padding: 24 }}>
                    <div className="live-video-col">
                        {session.faculty_summary && <p style={{ color: '#c7d4ea' }}>{session.faculty_summary}</p>}

                        {languageOptions.length > 1 && (
                            <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
                                <Form.Label className="small" style={{ color: '#c7d4ea' }}>Audio Language</Form.Label>
                                <Form.Select
                                    value={selectedLanguage || 'source'}
                                    onChange={e => changeLanguage(e.target.value === 'source' ? 'source' : e.target.value)}
                                    disabled={joining}
                                >
                                    {languageOptions.map(opt => (
                                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                                {selectedLanguage && selectedLanguage !== 'source' && (
                                    <p className="small mt-1 mb-0" style={{ color: GOLD }}>
                                        Translated audio may be delayed by several seconds.
                                    </p>
                                )}
                                {dubbingFellBack && (
                                    <p className="small mt-1 mb-0" style={{ color: '#f1a51c' }}>
                                        Dubbed audio is currently unavailable — playing the original audio instead.
                                    </p>
                                )}
                            </Form.Group>
                        )}

                        <div className="live-video-frame">
                            <div ref={audioContainerRef} className="live-video-target" />
                            {!joined && <span className="live-video-placeholder">Ready to join</span>}
                        </div>
                        {!joined ? (
                            <div style={{ textAlign: 'center' }}>
                                <button type="button" onClick={() => doJoin()} disabled={joining} style={{ marginTop: 16, background: GOLD, border: 'none', borderRadius: 8, padding: '12px 28px', fontWeight: 700 }}>
                                    {joining ? 'Joining…' : 'Join Live Session'}
                                </button>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <button
                                    type="button"
                                    onClick={toggleSpeaker}
                                    style={{
                                        marginTop: 16, borderRadius: 8, padding: '10px 24px', fontWeight: 700,
                                        background: speakerMuted ? '#f1a51c' : 'transparent',
                                        color: speakerMuted ? NAVY : '#fff',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                    }}
                                >
                                    {speakerMuted ? '🔇 Unmute' : '🔊 Mute'}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="live-side-col">
                        <LiveQAWidget slug={slug} sessionId={sessionId} />
                        <LiveTranscriptPanel slug={slug} sessionId={sessionId} language={selectedLanguage || 'source'} />
                    </div>
                </div>
            )}

            {session.status === 'completed' && (
                <div style={{ padding: 48, textAlign: 'center' }}>
                    <p style={{ fontSize: 18 }}>This session has ended.</p>
                    {(recordingUrl || translatedRecordings.length > 0) && (session.target_languages || []).length > 0 && (
                        <Form.Group className="mb-3 mx-auto" style={{ maxWidth: 320, textAlign: 'left' }}>
                            <Form.Label className="small" style={{ color: '#c7d4ea' }}>Recording Language</Form.Label>
                            <Form.Select value={playbackLanguage} onChange={e => setPlaybackLanguage(e.target.value)}>
                                <option value="source">Original ({sourceLabel(session.source_language)})</option>
                                {translatedRecordings.map(r => (
                                    <option key={r.language} value={r.language} disabled={r.status !== 'available'}>
                                        {LANGUAGE_LABEL[r.language] || r.language}{r.status !== 'available' ? ' (processing…)' : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    {activeRecordingUrl ? (
                        <video controls src={activeRecordingUrl} style={{ marginTop: 16, maxWidth: '100%', width: 720, borderRadius: 8, background: '#000' }} />
                    ) : (
                        <p style={{ color: '#c7d4ea' }}>
                            {session.recording_status === 'available'
                                ? 'A recording will be available shortly in your library.'
                                : 'Recording is still being processed and will appear in your library once ready.'}
                        </p>
                    )}
                </div>
            )}

            {session.status === 'cancelled' && (
                <div style={{ padding: 48, textAlign: 'center' }}>
                    <p style={{ fontSize: 18 }}>This session was cancelled.</p>
                    {session.cancelled_reason && <p style={{ color: '#c7d4ea' }}>{session.cancelled_reason}</p>}
                </div>
            )}

            <SessionFeedbackModal
                show={showFeedback}
                slug={slug}
                sessionId={sessionId}
                onClose={() => setShowFeedback(false)}
                onSubmitted={() => setShowFeedback(false)}
            />
        </div>
    );
};

export default JoinLivePage;
